import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";

export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  disabled = false,
  placeholder,
  allowEmpty = false,
  className,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  disabled?: boolean;
  placeholder?: string;
  allowEmpty?: boolean;
  className?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === "") {
      return options;
    }
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [query, filteredOptions.length]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  function selectOption(optionValue: string) {
    onChange(optionValue);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  }

  function commitActiveOption() {
    if (
      activeIndex >= 0 &&
      activeIndex < filteredOptions.length &&
      filteredOptions[activeIndex]
    ) {
      selectOption(filteredOptions[activeIndex].value);
    }
  }

  return (
    <div ref={rootRef} className={className}>
      {label && (
        <label
          className={`block text-sm font-medium ${disabled ? "text-gray-400" : "text-gray-500"}`}
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          className="w-full border-1 border-gray-300 rounded-xs p-2 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          disabled={disabled}
          placeholder={placeholder}
          type="text"
          value={open ? query : (selectedOption?.label ?? "")}
          onBlur={() => {
            setOpen(false);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onKeyDown={(e) => {
            if (disabled) {
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              commitActiveOption();
              return;
            }
            if (e.key === "Escape") {
              setOpen(false);
              setQuery("");
              setActiveIndex(-1);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (filteredOptions.length === 0) {
                return;
              }
              setOpen(true);
              setActiveIndex((i) =>
                i < filteredOptions.length - 1 ? i + 1 : 0,
              );
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (filteredOptions.length === 0) {
                return;
              }
              setOpen(true);
              setActiveIndex((i) =>
                i > 0 ? i - 1 : filteredOptions.length - 1,
              );
            }
          }}
        />
        {allowEmpty && !open && value !== "" && (
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
            type="button"
            onClick={() => onChange("")}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        {open && !disabled && filteredOptions.length > 0 && (
          <ul
            className="absolute z-20 mt-1 w-full max-h-40 overflow-y-auto border-1 border-gray-300 rounded-xs bg-white shadow-sm"
            role="listbox"
          >
            {filteredOptions.map((option, i) => (
              <li key={option.value}>
                <button
                  aria-selected={i === activeIndex || option.value === value}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${i === activeIndex ? "bg-gray-100" : ""}`}
                  role="option"
                  type="button"
                  onClick={() => selectOption(option.value)}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
